import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { SpecraOverlayDialog, SpecraTimeline } from '@mono/ui-components/captains';
import { SpecraOverlayFooter, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { registryHref } from '../../registryHref';
import { CorporateEventDialog } from './CorporateEventDialog';
import { corporateEventDates, corporateEventSentence } from './corporateEvents';
import { useCompanyLinks, useDeleteCorporateEvent } from './hooks';
const sortKey = (r)=>r.effectiveStart?.toDate().getTime() ?? r.effectiveEnd?.toDate().getTime() ?? 0;
/** Newest first; undated events last, so dated history reads in order. */ export function sortCorporateHistory(rows) {
    return [
        ...rows
    ].sort((a, b)=>{
        const ka = sortKey(a);
        const kb = sortKey(b);
        if (!ka !== !kb) return ka ? -1 : 1;
        return kb - ka;
    });
}
/**
 * The company's corporate history (REG-62): acquisitions, mergers, parents and
 * subsidiaries, JV membership, renames, spin-offs, novations, affiliates,
 * reseller channels and SBA mentor-protégé agreements, newest first. Manual
 * rows can be edited, ended or deleted; SBA rows are read-only.
 */ export function CorporateHistory({ companyId, companyName, lineage, canEdit }) {
    const links = useCompanyLinks(companyId);
    const del = useDeleteCorporateEvent();
    const [dialog, setDialog] = useState(undefined);
    const [confirmDelete, setConfirmDelete] = useState(undefined);
    const byId = useMemo(()=>new Map((links.data ?? []).map((r)=>[
                r.id,
                r
            ])), [
        links.data
    ]);
    const rows = sortCorporateHistory(lineage);
    const items = rows.map((r)=>{
        const other = r.other?.name || 'a company';
        const fromUs = r.direction === 'FROM_US';
        const sba = r.source === 'sba_mp_xlsx';
        const full = byId.get(r.relationshipId);
        const ended = !!r.effectiveEnd;
        const href = r.other ? registryHref(r.other) ?? undefined : undefined;
        return {
            id: r.relationshipId,
            title: corporateEventSentence(r.relationshipType, fromUs, other),
            date: corporateEventDates(r.effectiveStart, r.effectiveEnd),
            description: full?.notes || undefined,
            badge: sba ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                children: "From SBA"
            }) : ended ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                children: "Ended"
            }) : undefined,
            links: href ? [
                {
                    id: `${r.relationshipId}-other`,
                    label: `Open ${other}`,
                    href
                }
            ] : undefined,
            actions: canEdit && !sba && full ? /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                            label: `Actions for ${corporateEventSentence(r.relationshipType, fromUs, other)}`,
                            "data-testid": Testids.registryCompany.corporateEventMenu,
                            variant: "ghost",
                            size: "sm",
                            children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                                "aria-hidden": "true"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                        align: "end",
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setDialog({
                                        kind: 'edit',
                                        row: full,
                                        otherName: other
                                    }),
                                children: "Edit"
                            }),
                            !ended && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setDialog({
                                        kind: 'end',
                                        row: full,
                                        otherName: other
                                    }),
                                children: "End"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setConfirmDelete(r),
                                children: "Delete"
                            })
                        ]
                    })
                ]
            }) : undefined
        };
    });
    return /*#__PURE__*/ _jsxs("section", {
        "aria-labelledby": "corporate-history",
        "data-testid": Testids.registryCompany.corporateHistory,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Corporate history",
                headingId: "corporate-history",
                action: canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    size: "sm",
                    "data-testid": Testids.registryCompany.addCorporateEvent,
                    onClick: ()=>setDialog({
                            kind: 'add'
                        }),
                    children: "Add corporate event"
                }) : undefined
            }),
            /*#__PURE__*/ _jsx(SpecraTimeline, {
                variant: "dated",
                items: items,
                renderLink: renderRouterLink,
                emptyText: "No corporate events recorded yet. Acquisitions, mergers, parents, JVs and renames show here.",
                getItemTestId: ()=>Testids.registryCompany.corporateHistoryRow
            }),
            dialog && /*#__PURE__*/ _jsx(CorporateEventDialog, {
                open: true,
                onOpenChange: (o)=>!o && setDialog(undefined),
                companyId: companyId,
                companyName: companyName,
                mode: dialog
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: !!confirmDelete,
                onOpenChange: (o)=>!o && setConfirmDelete(undefined),
                size: "sm",
                title: "Delete this event?",
                description: "Delete only a mistake. To keep it as history, end it instead.",
                children: /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                    confirmLabel: "Delete",
                    loading: del.isPending,
                    onConfirm: ()=>{
                        if (!confirmDelete) return;
                        del.mutate(confirmDelete.relationshipId, {
                            onSuccess: ()=>setConfirmDelete(undefined)
                        });
                    },
                    onCancel: ()=>setConfirmDelete(undefined)
                })
            })
        ]
    });
}
